import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type {
  ApiResponse,
  IdentityBoard,
  BoardCompleteness,
} from "@/api/types";
import {
  ArrowRight,
  CheckCircle,
  Lock,
  Sparkles,
  Users,
  Edit3,
} from "lucide-react";
import clsx from "clsx";
import { CATEGORY_CONFIG } from "@/constants/categoryConfig";

export default function BoardOverview() {
  const { data: boardData, isLoading: boardLoading } = useQuery({
    queryKey: ["identity-board"],
    queryFn: () => api.get<ApiResponse<IdentityBoard>>("/identity-board"),
  });

  const { data: completenessData } = useQuery({
    queryKey: ["identity-board-completeness"],
    queryFn: () =>
      api.get<ApiResponse<BoardCompleteness>>("/identity-board/completeness"),
    enabled: !!boardData,
  });

  const board = boardData?.data;
  const completeness = completenessData?.data;

  if (boardLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // No board yet - redirect to create
  if (!board) {
    return (
      <div className="max-w-md mx-auto py-20 text-center">
        <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <Sparkles className="w-8 h-8 text-primary-600" />
        </div>
        <h2 className="text-2xl font-semibold mb-3">No Board Yet</h2>
        <p className="text-gray-600 mb-6">
          Let's create your Identity Board to start finding your people.
        </p>
        <Link
          to="/lab/identity-board/create"
          className="btn-primary inline-flex items-center gap-2"
        >
          Create Your Board
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const discoveryUnlocked = board.discoveryUnlocked;
  const completenessPercent = completeness?.totalScore || 0;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-display font-bold">My Identity Board</h1>
          <p className="text-gray-600">
            {discoveryUnlocked
              ? "Your board is complete! Browse your matches."
              : `Complete ${3 - board.blocksCompleted} more block${3 - board.blocksCompleted > 1 ? "s" : ""} to unlock Discovery`}
          </p>
        </div>
        <Link
          to="/lab/identity-board/create"
          className="btn-outline flex items-center gap-2"
        >
          <Edit3 className="w-4 h-4" />
          Edit Board
        </Link>
      </div>

      {/* Progress Bar */}
      <div className="card p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">
            Board Completeness
          </span>
          <span className="text-sm font-semibold text-primary-600">
            {completenessPercent}%
          </span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-bar-fill"
            style={{ width: `${completenessPercent}%` }}
          />
        </div>
        <div className="flex items-center gap-2 mt-3">
          {discoveryUnlocked ? (
            <>
              <CheckCircle className="w-4 h-4 text-twins" />
              <span className="text-sm text-twins-dark font-medium">
                Discovery Unlocked!
              </span>
            </>
          ) : (
            <>
              <Lock className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">
                {board.blocksCompleted}/3 blocks needed for Discovery
              </span>
            </>
          )}
        </div>
      </div>

      {/* Discovery CTA */}
      {discoveryUnlocked && (
        <Link
          to="/lab/identity-board/discovery"
          className="card p-6 mb-6 bg-gradient-to-r from-primary-500 to-accent-500 text-white flex items-center justify-between group hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Discover Your Matches</h3>
              <p className="text-white/80 text-sm">
                See people who share your background and interests
              </p>
            </div>
          </div>
          <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
        </Link>
      )}

      {/* Blocks Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {CATEGORY_CONFIG.map((block) => {
          const Icon = block.icon;
          const blockData = completeness?.breakdown[block.key];
          const isComplete = blockData && blockData.filled.length > 0;

          const colorClasses: Record<
            string,
            { bg: string; text: string; light: string }
          > = {
            primary: {
              bg: "bg-primary-100",
              text: "text-primary-600",
              light: "bg-primary-50",
            },
            accent: {
              bg: "bg-accent-100",
              text: "text-accent-600",
              light: "bg-accent-50",
            },
            twins: {
              bg: "bg-twins-light",
              text: "text-twins-dark",
              light: "bg-green-50",
            },
            strong: {
              bg: "bg-strong-light",
              text: "text-strong-dark",
              light: "bg-blue-50",
            },
          };

          const colors = colorClasses[block.color];

          return (
            <div
              key={block.key}
              className={clsx("card p-5", isComplete && colors.light)}
            >
              <div className="flex items-start gap-4">
                <div
                  className={clsx(
                    "w-12 h-12 rounded-xl flex items-center justify-center",
                    colors.bg,
                    colors.text,
                  )}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{block.label}</h3>
                    {isComplete && (
                      <CheckCircle className="w-4 h-4 text-twins" />
                    )}
                  </div>
                  {blockData ? (
                    <div className="text-sm text-gray-600">
                      {blockData.filled.length > 0 ? (
                        <span>{blockData.filled.length} fields completed</span>
                      ) : (
                        <span className="text-gray-400">Not started</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">Loading...</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
